import { of as observableOf, Subject, Observable, of, Subscriber } from 'rxjs';
import { Injectable } from "@angular/core";
import { SabisTranslateService } from '../translation/sdp-translate.service';
import { SabisToaster2Service } from '../sabis-toaster2/toaster.service';
import { SabisNgxNotificationsModule } from './sabis-notifications.module';
import { NotificationDialogArgs, SabisNgxNotificationDialogComponent } from './notification-dialog/notification-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DialogType, NotificationArgs } from './models';



@Injectable({
  providedIn: SabisNgxNotificationsModule
})
export class SabisNgxNotificationService {
  private onRequestConfirmationSubject: Subject<NotificationArgs> = new Subject();
  onShowNotificationNotice: Subject<NotificationArgs> = new Subject();

  onRequestConfirmation: Observable<NotificationArgs> = this.onRequestConfirmationSubject.asObservable();

  constructor(private _translate: SabisTranslateService,
    public dialog: MatDialog,
    private _sabisToasterService: SabisToaster2Service) { }

  public initialize(): void {
    //  listen to confirmation request
    this.onRequestConfirmation.subscribe(r => {
      this.showNotificationInternal(r, true);
    });

    this.onShowNotificationNotice.subscribe(args => {
      this.showNotificationInternal(args, false);
    });
  }

  private showNotificationInternal(args: NotificationArgs, isConfirmation: boolean): void {
    args.result = new Observable((s: Subscriber<boolean>) => {
      var dialogArgs: NotificationDialogArgs = {
        title: args.title,
        messages: args.message,
        type: args.type,
        confirmationData: isConfirmation ? {
          confirmationButtonName: args.confirmButtonName,
          discardButtonName: args.discardButtonName
        } : null
      };
      var dialog = this.dialog.open(SabisNgxNotificationDialogComponent, {
        hasBackdrop: true,
        panelClass: 'sc-modal',
        data: dialogArgs
      });
      dialog.afterClosed().subscribe(r => {
        s.next(!!r);
        s.complete();
      });
    });
  }

  /**
   * Show confirmation dialog, with "Yes/No" buttons
   * @param message specify the text message to display
   */
  public confirm(message: string | string[]): Observable<boolean>;
  /**
   * Show confirmation dialog, with "Yes/No" buttons
   * @param message specify the text message to display
   * @param title specify the dialog title
   */
  public confirm(message: string | string[], title: string): Observable<boolean>;
  /**
   * 
   * @param message
   * @param title
   * @param confirmButtonName
   */
  public confirm(message: string | string[], title: string, confirmButtonName: string, discardButtonName?: string): Observable<boolean>;
  confirm(message: string | string[], title?: string, confirmButtonName?: string, discardButtonName?: string): Observable<boolean> {
    var args: NotificationArgs = {
      title: title ? title : this._translate.instant('Notification.ConfirmTitle'),
      message: message,
      result: observableOf(false),
      type: DialogType.Notification,
      confirmButtonName: confirmButtonName ? confirmButtonName : this._translate.instant('Yes'),
      discardButtonName: discardButtonName ? discardButtonName : this._translate.instant('No'),
    };
    this.onRequestConfirmationSubject.next(args);

    return args.result;
  }

  /**
   * Show confirmation dialog, with "Yes/No" buttons
   * @param messageKey specify the text message resource key
   */
  public confirmResource(messageKey: string): Observable<boolean>;
  /**
   * Show confirmation dialog, with "Yes/No" buttons
   * @param messageKey specify the text message resource key
   * @param titleKey specify the dialog title resource key
   */
  public confirmResource(messageKey: string, titleKey: string): Observable<boolean>;
  confirmResource(messageKey: string, titleKey?: string): Observable<boolean> {
    let message = this._translate.instant(messageKey);
    let title = titleKey ? this._translate.instant(titleKey) : '';

    return this.confirm(message, title);
  }

  /**
   * Show notification toast
   * @param message specify the text message to display
   */
  public showNotification(message: string): void;
  /**
   * Show notification toast
   * @param message specify the text message to display
   * @param title specify the dialog title
   */
  public showNotification(message: string, title: string): void;
  showNotification(message: string, title?: string): void {
    const toasterConfig = {
      disableTimeOut: false,
      timeOut: 7000
    };
    this._sabisToasterService.showInfo(message, title ? title : this._translate.instant('Notification.Notification'), toasterConfig);
  }

  /**
   * Show notification toast
   * @param messageKey specify the message resource key to translate and display
   */
  public showNotificationResource(messageKey: string): void;
  /**
   * Show notification toast
   * @param messageKey specify the message resource key to translate and display
   * @param title specify the title resource key to translate and display
   */
  public showNotificationResource(messageKey: string, titleKey: string): void;
  showNotificationResource(messageKey: string, titleKey?: string): void {
    let title = titleKey ? this._translate.instant(titleKey) : '';
    this.showNotification(this._translate.instant(messageKey), title);
  }

  /**
   * Show alert toast
   * @param message specify the text message to display
   */
  public showAlert(message: string): void;

  /**
   * Show alert toast
   * @param message specify the text message to display
   * @param title specify the dialog title
   */
  public showAlert(message: string, title: string): void;
  showAlert(message: string, title?: string): void {
    const toasterConfig = {
      disableTimeOut: true
    };
    this._sabisToasterService.showError(message, title ? title : this._translate.instant('Notification.Alert'), toasterConfig);
  }


  /**
   * Show alert toast
   * @param messageKey specify the message resource key to translate and display
   */
  public showAlertResource(messageKey: string): void;
  /**
   * Show alert toast
   * @param messageKey specify the message resource key to translate and display
   * @param titleKey specify the title resource key to translate and display
   */
  public showAlertResource(messageKey: string, titleKey: string): void;
  showAlertResource(messageKey: string, titleKey?: string): void {
    let title = titleKey ? this._translate.instant(titleKey) : '';

    this.showAlert(this._translate.instant(messageKey), title);
  }

  /**
   * Show alert popup
   * @param message specify the text message to display
   */
  public showAlertNotice(message: string): Observable<boolean>;
  /**
   * Show alert popup
   * @param message specify the text message to display
   * @param title specify the dialog title
   */
  public showAlertNotice(message: string, title: string): Observable<boolean>;
  showAlertNotice(message: string, title?: string): Observable<boolean> {
    const args = {
      title: title ? title : this._translate.instant('Notification.Alert'),
      message: message,
      type: DialogType.Alert,
      result: of(false),
      confirmButtonName: this._translate.instant('Yes'),
      discardButtonName: this._translate.instant('No')
    };
    this.onShowNotificationNotice.next(args);
    return args.result;
  }

  /** Notify a user that a save operation has succeeded */
  public saveSucceeded() {
    this._sabisToasterService.showSuccess(this._translate.instant('Notification.SavedSuccessfully'), this._translate.instant('Notification.Succeeded'));
  }

  /**
 * Show Green notification toast
 * @param messageKey specify the message resource key to translate and display
 * @param title specify the title resource key to translate and display
 */
  public showSuccessNotificationResource(message: string): void;
  showSuccessNotificationResource(message: string): void {
    this._sabisToasterService.showSuccess(message, this._translate.instant('Notification.Succeeded'));
  }


  /** Notify the user that a save operation has succeded */
  public saveSucceededNotice(): Observable<boolean> {
    return this.showNotificationNotice(this._translate.instant('Notification.SavedSuccessfully'), this._translate.instant('Notification.Succeeded'));
  }

  /**
   * Show notification dialog
   * @param message specify the message to display
   */
  public showNotificationNotice(message: string): Observable<boolean>;

  /**
  * Show notification dialog
  * @param message specify the message to display
  * @param title specify the title to display
  */
  public showNotificationNotice(message: string, title: string): Observable<boolean>;
  showNotificationNotice(message: string, title?: string): Observable<boolean> {
    var args: NotificationArgs = {
      title: title ? title : this._translate.instant('Notification.Notification'),
      message: message,
      type: DialogType.Notification,
      result: of(false),
      confirmButtonName: this._translate.instant('Yes'),
      discardButtonName: this._translate.instant('No'),
    };
    this.onShowNotificationNotice.next(args);

    return args.result;
  }

  public clearMessages(): void {
    this._sabisToasterService.closeAllToasts();
  }
}

